import { AIProvider } from '@pique/core/blocksuite/presets/ai';
import { posthog } from '@pique/core/posthog';
import { OAuthProviderType } from '@pique/graphql';
import {
  ApplicationFocused,
  ApplicationStarted,
  createEvent,
  OnEvent,
  Service,
} from '@toeverything/infra';

import { commands as authCommands } from '../../../tauri/plugins/auth';
import { type AuthAccountInfo, AuthSession } from '../entities/session';
import type { AuthStore } from '../stores/auth';
import {
  isProductionDesktop,
  refreshTauriAuthToken,
  signInOauthViaLocalServer,
} from '../utils/tauri-auth';
import type { FetchService } from './fetch';

// Emit when account changed
export const AccountChanged = createEvent<AuthAccountInfo | null>(
  'AccountChanged'
);

export const AccountLoggedIn = createEvent<AuthAccountInfo>('AccountLoggedIn');

export const AccountLoggedOut =
  createEvent<AuthAccountInfo>('AccountLoggedOut');

function toAIUserInfo(account: AuthAccountInfo | null) {
  if (!account) return null;
  return {
    avatarUrl: account.avatar ?? '',
    email: account.email ?? '',
    id: account.id,
    name: account.label,
  };
}

@OnEvent(ApplicationStarted, e => e._onApplicationStart)
@OnEvent(ApplicationFocused, e => e._onApplicationFocused)
export class AuthService extends Service {
  session = this.framework.createEntity(AuthSession);

  constructor(
    private readonly fetchService: FetchService,
    private readonly store: AuthStore
  ) {
    super();

    AIProvider.provide('userInfo', () => {
      return toAIUserInfo(this.session.account$.value);
    });

    let previousAccountId: string | null | undefined = undefined;

    this.session.account$.subscribe(account => {
      const currentId = account?.id ?? null;
      const isInitial = previousAccountId === undefined;
      const hasChanged = previousAccountId !== currentId;

      if (isInitial) {
        if (account) {
          (window as any).__posthog?.identify(account.email, {
            name: account.label,
          });
        }
      } else if (hasChanged) {
        if (account === null) {
          this.eventBus.emit(AccountLoggedOut, account);
          (window as any).__posthog?.reset();
        } else {
          this.eventBus.emit(AccountLoggedIn, account);
          (window as any).__posthog?.identify(account.email, {
            name: account.label,
          });
          posthog.capture('sign_in_completed');
        }
        this.eventBus.emit(AccountChanged, account);
      }

      previousAccountId = currentId;
      AIProvider.slots.userInfo.emit(toAIUserInfo(account));
    });
  }

  async _onApplicationStart() {
    // Tauri path uses Authorization header via vault token; no cookie sync
    if (environment.isTauri) {
            try {
        const userId = await authCommands.getFromStore('auth-user-id');
        if (userId) {
          try {
                        await authCommands.initVault(userId);
          } catch {
            /* noop */
          }
          // Warm the token cache
          const { refreshTauriAuthToken } = await import('../utils/tauri-auth');
                    await refreshTauriAuthToken();
        }
      } catch (error) {
        console.error('Tauri init error on start:', error);
      }
    }

    this.session.revalidate();
  }

  _onApplicationFocused() {
    this.session.revalidate();
  }

  async signInOauth(
    provider: OAuthProviderType,
    redirectUri?: string | null,
    options?: {
      forceConsent?: boolean;
    }
  ) {
    if (provider === OAuthProviderType.Apple) {
      if (environment.isTauri) {
        if (!(await isProductionDesktop())) {
          throw new Error(
            'Apple sign-in requires a signed desktop build. Use Google in development.'
          );
        }
        const url = new URL(
          `${runtimeConfig.serverUrlPrefix}/oauth/apple/login`
        );
        url.searchParams.set('redirect_uri', 'pique://auth/callback');
        url.searchParams.set('client', 'desktop');
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await openUrl(url.toString());
        return;
      }
      const appleUrl = new URL(
        `${runtimeConfig.serverUrlPrefix}/oauth/apple/login`
      );
      appleUrl.searchParams.set('redirect_uri', redirectUri ?? location.pathname);
      location.href = appleUrl.toString();
      return;
    }

    if (environment.isTauri) {
      const isProduction = await isProductionDesktop();
      if (isProduction) {
        // Production: deep link (shows "app.pique.so wants to open Pique")
        const url = new URL(`${runtimeConfig.serverUrlPrefix}/oauth/login`);
        url.searchParams.set('provider', String(provider));
        url.searchParams.set('redirect_uri', 'pique://auth/callback');
        url.searchParams.set('client', 'desktop');
        url.searchParams.set('scopes', 'calendar');
        if (options?.forceConsent) url.searchParams.set('force_consent', '1');
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await openUrl(url.toString());
      } else {
        // Dev/Nightly: localhost server flow
        await signInOauthViaLocalServer(
          runtimeConfig.serverUrlPrefix,
          String(provider),
          options
        );
        // Full reload clears all module state and re-triggers bootstrap
        // which navigates based on activeWorkspaceId
        console.log('[auth] desktop dev OAuth complete, reloading to /');
        window.location.href = '/';
      }
      return;
    }

    const url = new URL(`${runtimeConfig.serverUrlPrefix}/oauth/login`);
    url.searchParams.set('provider', String(provider));
    url.searchParams.set('redirect_uri', redirectUri ?? location.pathname);
    url.searchParams.set('scopes', 'calendar');
    if (options?.forceConsent) {
      url.searchParams.set('force_consent', '1');
    }
    location.href = url.toString();
  }

  signInOauthInApp(
    provider: OAuthProviderType,
    options?: { forceConsent?: boolean }
  ) {
    const url = new URL(`${runtimeConfig.serverUrlPrefix}/oauth/login`);
    url.searchParams.set('provider', String(provider));
    url.searchParams.set('redirect_uri', '/auth/signIn');
    url.searchParams.set('scopes', 'calendar');
    if (options?.forceConsent) {
      url.searchParams.set('force_consent', '1');
    }
    location.href = url.toString();
  }

  async completeOAuthFromDeepLink(sessionId: string): Promise<void> {
    if (!environment.isTauri) return;

    await authCommands.setInVault('remote-server', sessionId);
    await refreshTauriAuthToken();
    // Full reload clears all module state and re-triggers bootstrap
    // which navigates based on activeWorkspaceId
    console.log('[auth] deep link OAuth complete, reloading to /');
    window.location.href = '/';
  }

  async signOut() {
    try {
      await this.fetchService.fetch('/api/auth/sign-out');
    } catch (error) {
      // Check if it's a 401 - this means we're already signed out on the server
      // For any error, log but still proceed with local cleanup
      const err = error as { status?: number; message?: string };
      if (err?.status !== 401 && !err?.message?.includes('401')) {
        console.error(
          'Sign-out request failed, proceeding with local cleanup anyway:',
          error
        );
      }
      // Never throw - always proceed to clean up local state
    }

    posthog.capture('sign_out_completed');

    // ALWAYS clear local session state, regardless of server response
    this.store.setCachedAuthSession(null);

    // Pique Update - for Tauri
    if (environment.isTauri) {
      try {
        const userId = await authCommands.getFromStore('auth-user-id');
        if (userId) {
          await authCommands.initVault(userId);
          await authCommands.setInVault('remote-server', '');
          await refreshTauriAuthToken();
        }
      } catch (e) {
        console.error('Failed to clear vault on signout', e);
        // Don't throw here - Tauri vault cleanup failure shouldn't block sign out
      }
    }
    // Pique Update finished

    // ALWAYS revalidate session to clear UI state
    this.session.revalidate();
  }
}
